import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/api';
import AthleteHeadshot from '../AthleteHeadshot';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Summary cards for the active team, built from existing endpoints (no new
 * API needed for v1). Quick-action buttons jump to the relevant sub-tab.
 */
export default function TeamOverview({ team, onNavigate }) {
  const { token } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    rosterCount: 0,
    injuredCount: 0,
    squadCount: 0,
    unassignedCount: 0,
    pendingInvites: 0
  });

  useEffect(() => {
    if (team) fetchStats();
  }, [team]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [athletesRes, squadsRes, invitesRes] = await Promise.all([
        fetch(`${API_URL}/api/athletes?team=${encodeURIComponent(team)}`, { headers: getAuthHeaders(token) }),
        fetch(`${API_URL}/api/squads?team=${encodeURIComponent(team)}`, { headers: getAuthHeaders(token) }),
        fetch(`${API_URL}/api/invitations?team=${encodeURIComponent(team)}`, { headers: getAuthHeaders(token) })
      ]);

      const athletes = athletesRes.ok ? await athletesRes.json() : [];
      const squadsData = squadsRes.ok ? await squadsRes.json() : { squads: [], members: [] };
      const invitesData = invitesRes.ok ? await invitesRes.json() : { invitations: [] };

      const squadMembers = squadsData.members || [];
      const unassignedCount = squadMembers.filter((m) => m.squadId == null).length;

      setStats({
        rosterCount: athletes.length,
        injuredCount: athletes.filter((a) => a.is_injured).length,
        squadCount: (squadsData.squads || []).length,
        unassignedCount,
        pendingInvites: (invitesData.invitations || []).filter((i) => !i.isExpired).length
      });
      setAthletes(athletes);
    } catch (error) {
      console.error('Error fetching team overview stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { label: 'Roster', value: stats.rosterCount, action: 'roster', color: 'bg-gray-50' },
    { label: 'Injured', value: stats.injuredCount, action: 'roster', color: stats.injuredCount > 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50' },
    { label: 'Squads', value: stats.squadCount, action: 'squads', color: 'bg-gray-50' },
    { label: 'Unassigned to a squad', value: stats.unassignedCount, action: 'squads', color: 'bg-gray-50' },
    { label: 'Pending Invites', value: stats.pendingInvites, action: 'invites', color: 'bg-gray-50' },
  ];

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-3">{team} Overview</h3>
      {loading ? (
        <p className="text-gray-500 text-sm">Loading overview...</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {cards.map((card) => (
            <button
              key={card.label}
              onClick={() => onNavigate?.(card.action)}
              className={`p-4 rounded text-center hover:brightness-95 transition ${card.color}`}
            >
              <div className="text-2xl font-bold">{card.value}</div>
              <div className="text-sm text-gray-600">{card.label}</div>
            </button>
          ))}
        </div>
      )}

      {!loading && athletes.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-600 mb-3">Roster photos</h4>
          <div className="flex flex-wrap gap-3">
            {athletes.slice(0, 12).map((athlete) => (
              <div key={athlete.id} className="flex flex-col items-center w-16">
                <AthleteHeadshot name={athlete.name} headshotUrl={athlete.headshot_url} size="md" />
                <p className="mt-1 text-[11px] text-gray-600 text-center leading-tight truncate w-full">{athlete.name.split(' ')[0]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => onNavigate?.('roster')} className="btn-theme-secondary px-4 py-2 rounded text-sm">
          Manage Roster
        </button>
        <button onClick={() => onNavigate?.('squads')} className="btn-theme-secondary px-4 py-2 rounded text-sm">
          Manage Squads
        </button>
        <button onClick={() => onNavigate?.('invites')} className="btn-theme-secondary px-4 py-2 rounded text-sm">
          Invite Members
        </button>
      </div>
    </div>
  );
}
